import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { Redirect } from 'react-router-dom';

import pluralize from 'pluralize';
import {
  formattingHelpers,
  bookingActions,
  LoadingView,
  scrollingHelpers
} from 'day8-react';
import { Svg } from 'day8-react/build/new-components';

import moment from 'moment';
import Konami from 'react-konami';

import { checkoutBooking, cancelBooking } from '../../actions/booking';
import { BOOKING } from '../../constants';
import PaymentOptions from '../../components/booking/PaymentOptions/PaymentOptions';
import { analyticsHelpers } from 'day8-ui';
import { getQuery, updateSearch } from '../../helpers/querystring';
import history from '../../history';
import { withBookingBasket } from '../../context/BookingBasketContext';

class Payment extends Component {
  static _getFirstInstalment(order) {
    if (order.orderDeadlines.length) {
      return order.orderDeadlines[0];
    }
    return {
      dueDate: order.dueDate,
      amount: order.gross,
      percent: 100
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      amount: this.props.booking.mainOrder.gross,
      forceAllowPayLater: false
    };
    this.onError = this.onError.bind(this);
  }

  componentDidMount() {
    scrollingHelpers.scrollToTop();
    this.updateBodyScroll();
    // Fire begin_checkout on each genuine landing on the checkout page - it re-fires if the user
    // abandons and comes back to the same (still-active) booking, since the component remounts.
    // Skip it when returning from the 3DS/payment redirect (cko-session-id in the url), as that
    // is a payment check, not a fresh checkout entry.
    const query = getQuery(this.props.location);
    if (!query['cko-session-id'] && this.props.booking) {
      analyticsHelpers.beginCheckout(this.props.booking);
    }
  }

  componentDidUpdate(prevProps) {
    const bookingStatus = this.props.booking.status;
    const prevBookingStatus = prevProps.booking.status;
    if (bookingStatus !== prevBookingStatus && [BOOKING.OK_STATUS, BOOKING.DEFERRED_STATUS].includes(bookingStatus)) {
      analyticsHelpers.bookingComplete(this.props.booking);
    }
    if (this.props.booking && this.props.booking.id !== prevProps.booking.id) {
      history.push("/browse");
    }

    // Handle scroll lock for promo overlay
    const wasShowingOverlay = this.isPromoOverlayVisible(prevProps);
    const isShowingOverlay = this.isPromoOverlayVisible();
    if (wasShowingOverlay !== isShowingOverlay) {
      this.updateBodyScroll();
    }
  }

  componentWillUnmount() {
    // Ensure scroll is restored when component unmounts
    document.body.style.overflow = '';
  }

  isPromoOverlayVisible(props = this.props) {
    const { booking, isRefetchingBooking } = props;
    if (!booking) {
      return false;
    }
    return !booking.hasPromoCodeApplied && isRefetchingBooking;
  }

  updateBodyScroll() {
    if (this.isPromoOverlayVisible()) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  static onPaymentSuccess(payment) {
    console.log('PAYMENT SUCCESS', payment);
  }

  onError() {
    const { bookingBasketContext } = this.props;
    // clear CKO from react router history - should fix buf when it kept coming back from react router when changing payment methods
    const query = getQuery(history.location);
    if (query['cko-session-id']) {
      history.push({
        pathname: history.location.pathname,
        search: updateSearch(history.location, { "cko-session-id": undefined })
      });
    }
    // Booking wouldn't have timed out whilst it had pending
    // payment on it so might need to trigger timeout here
    const now = moment.utc(new Date());
    const timeout = moment.utc(bookingBasketContext.timeoutDateTime);
    if (!bookingBasketContext.isTimingOut && timeout < now) {
      bookingBasketContext.timeoutBooking();
    }
  }

  render() {
    const { user, booking, isYachtsAndFriends, bookingBasketContext } = this.props;
    const { forceAllowPayLater } = this.state;

    if (bookingBasketContext.isTimingOut) {
      return <LoadingView title="Booking timed out, redirecting..." />;
    }

    const order = booking.mainOrder;
    const firstInstalment = Payment._getFirstInstalment(order);
    const email = user.email;
    const girls = pluralize('girl', booking.numGirls, true);
    const guys = pluralize('guy', booking.numBoys, true);

    if ([BOOKING.OK_STATUS, BOOKING.DEFERRED_STATUS].includes(booking.status)) {
      return (
        <Redirect
          to={`/account/booking/${booking.id}/success-${booking.subject}`}
        />
      );
    }

    const loadingAutoPromocodeOverlay = this.isPromoOverlayVisible() && (
      <div className='dui-fixed dui-inset-0 dui-z-50 dui-flex dui-items-center dui-justify-center dui-bg-black/50 dui-backdrop-blur-sm'>
        <div className='dui-bg-white dui-p-10 dui-mx-4 dui-max-w-md dui-text-center dui-animate-fade-in'>
          <div className='dui-flex dui-justify-center dui-mb-8'>
            <div className='dui-relative'>
              <Svg
                name={'sea-transport-wheel'}
                className={'d8-u-spinreverse-animation icon--xl'}
                fill={'#182c4b'}
              />
            </div>
          </div>
          <h2 className='dui-text-xl md:dui-text-2xl dui-font-bold dui-mb-3'>
            🎉 Searching for discounts
          </h2>
          <p className='dui-text-base dui-text-gray-600 dui-mb-4'>
            We&apos;re checking if there are any promotions available for your booking
          </p>
        </div>
      </div>
    );

    let allowPayLater = true;
    const deadlinesExist = !!order.orderDeadlines.length;
    if (!forceAllowPayLater) {
      if (deadlinesExist) {
        const firstDeadline = order.orderDeadlines[0];
        if (moment().utc().isSame(firstDeadline.dueDate, 'day')) {
          allowPayLater = false;
        }
      } else if (moment().utc().isSame(order.dueDate, 'day')) {
        allowPayLater = false;
      }
      // Not allowing pay later on custom yachts
      // if (booking.bookingType === BOOKING.NORMAL_TYPE && booking.subject === BOOKING.YACHT_SUBJECT) {
      //   allowPayLater = false;
      // }
    }
    let firstDeadlineDays = 0;
    if (deadlinesExist) {
      const firstDeadlineDueDate = order.orderDeadlines[0].dueDate;
      firstDeadlineDays = moment(firstDeadlineDueDate).diff(moment().startOf('day'), 'days');
    }
    const firstInstallmentPercent = deadlinesExist && formattingHelpers.roundIfNeeded(firstInstalment.percent, 2);
    return (
      <div>
        <Konami
          easterEgg={() => this.setState({ forceAllowPayLater: true })}
          konami={[83, 75, 73, 80, 80, 65, 89]}
        />
        {loadingAutoPromocodeOverlay}
        <PaymentOptions
          location={this.props.location}
          history={this.props.history}
          booking={booking}
          onError={this.onError}
          createPayment={this.props.createPayment}
          checkPaymentInfo={this.props.checkPaymentInfo}
          onPaymentSuccess={Payment.onPaymentSuccess}
          email={email}
          girls={girls}
          guys={guys}
          minAge={booking?.destination?.ageLimit || 20}
          isYachtsAndFriends={isYachtsAndFriends}
          companyId={this.props.companyId}
          allowPayLater={allowPayLater}
          checkoutBooking={this.props.checkoutBooking}
          firstInstallmentPercent={firstInstallmentPercent}
          forcePayLater={forceAllowPayLater}
          firstDeadlineDays={firstDeadlineDays}
          hasPromoCodeApplied={booking?.hasPromoCodeApplied}
        />
      </div>
    );
  }
}

Payment.propTypes = {
  booking: PropTypes.object,
  user: PropTypes.object,
  createPayment: PropTypes.func.isRequired,
  checkPaymentInfo: PropTypes.func.isRequired,
  checkoutBooking: PropTypes.func.isRequired,
  cancelBooking: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  companyId: PropTypes.number,
  isYachtsAndFriends: PropTypes.bool,
  isRefetchingBooking: PropTypes.bool,
  bookingBasketContext: PropTypes.object
};

const mapStateToProps = store => ({
  user: store.user.loggedUser,
  booking: store.booking.currentBooking
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      createPayment: bookingActions.createPayment,
      checkPaymentInfo: bookingActions.checkPaymentInfo,
      checkoutBooking,
      cancelBooking
    },
    dispatch
  );

export default withBookingBasket(connect(mapStateToProps, mapDispatchToProps)(Payment));
